import { Box, SelectChangeEvent, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import SelectInput from "./UIElements/select";
import useResponsive from "../hooks/useResponsive";
import { limitToSeven } from "../utils/url-id";

const headerSty = {
  fontFamily: "Lexend",
  letterSpacing: "0.01em",
  fontWeight: 400,
};
const contentSty = {
  fontFamily: "Lexend",
  letterSpacing: "0.01em",
  fontWeight: 400,
};

interface ProfileUIProps {
  name: string;
  email: string;
  staffId: string;
  status: string;
  flagReason: string;
  shiftId: string | number;
}
export const ApprasialProfileUI = ({
  name,
  email,
  staffId,
  status,
  shiftId,
  flagReason,
}: ProfileUIProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: isMedium ? 2 : 3,
        paddingY: 2,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 62,
      }}
    >
      <Stack sx={{ marginX: 3, marginY: isMedium ? 2 : 0 }}>
        <img
          src="/assets/icons/employee-icon.png"
          alt=""
          width={60}
          height={60}
        />
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Name
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {name}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Email
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {email}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Staff ID
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {staffId}
        </Typography>
      </Stack>{" "}
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Status
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {status}
        </Typography>
      </Stack>{" "}
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          ShiftId
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {limitToSeven(shiftId?.toString())}
        </Typography>
      </Stack>{" "}
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Flagged Note
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {flagReason ?? "-"}
        </Typography>
      </Stack>{" "}
    </Box>
  );
};

interface PerformanceUIProps {
  rating: string;
  last_date: string;
  next_appraisal_date: string;
  appraisal_cycle: string;
}
export const PerformanceUI = ({
  rating,
  last_date,
  next_appraisal_date,
  appraisal_cycle,
}: PerformanceUIProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: isMedium ? 1 : 3,
        paddingY: isMedium ? 2 : "1.3rem",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 62,
      }}
    >
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Overall Rating
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {rating}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Last Appraisal Date
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {last_date}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Next Appraisal Date:
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {next_appraisal_date}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Appraisal Cycle
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {appraisal_cycle}
        </Typography>
      </Stack>
    </Box>
  );
};

interface PerformanceMetricsProps {
  patient_satisfaction: string;
  attendance: string;
  task_completion: string;
  kpi: string;
}
export const PerformanceMetricsUI = ({
  patient_satisfaction,
  attendance,
  task_completion,
  kpi,
}: PerformanceMetricsProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: isMedium ? 2 : 3,
        paddingY: isMedium ? 2 : "1.3rem",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 62,
      }}
    >
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Patient Satisfaction
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {patient_satisfaction}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Attendance
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {attendance}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Task Completion Rate
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {task_completion}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          KPI Score
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {kpi}
        </Typography>
      </Stack>
    </Box>
  );
};

export const FeedbackCommemts = () => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        background: "#FAFBFC",
        borderRadius: 2,
        paddingY: 3,
        paddingX: 3,
      }}
    >
      <Stack>
        <Typography
          variant="subtitle2"
          color={"neutral"}
          sx={{ fontFamily: "Inter", fontWeight: 700, mb: 2 }}
        >
          Task Completion Excellence
        </Typography>
        <Typography
          color={"neutral"}
          sx={{
            fontSize: "11px",
            fontWeight: 200,
            lineHeight: "24px",
            letterSpacing: "1%",
          }}
        >
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Dolor
          malesuada velit et, vulputate congue augue vestibulum est. Ac pulvinar
          viverra et ultrices. Ornare.
        </Typography>
        <Stack direction={"column"} alignItems={"flex-end"}>
          <Typography
            variant="subtitle2"
            color={"neutral"}
            sx={{ fontFamily: "Lexend", fontWeight: 700, letterSpacing: "1%" }}
          >
            Jermy Carson
          </Typography>
          <Typography
            color={"neutral"}
            sx={{
              fontSize: "11px",
              fontWeight: 200,
              lineHeight: "12px",
              letterSpacing: "1%",
            }}
          >
            Line manager
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
